// Runtime i18n resolver. Replaces the old build-time inlining: messages are resolved against the
// currently selected locale at runtime, so the UI language can be switched live. All locale tables
// are bundled (see locales.js). Resolution always falls back current locale → en-US → the raw key,
// so a missing or broken translation can never render as empty text.
//
// Keys in the locale tables are stored WITHOUT the `intl.` prefix (e.g. `loading`, `errorShort`);
// callers may pass them with or without the prefix (the loader emits them bare).
import parse from 'format-message-parse'
import interpret from 'format-message-interpret'
import { LOCALE_TABLES, DEFAULT_LOCALE } from './locales.js'

// The active locale for imperative (script-side) lookups. Kept in sync with store.locale by an
// observer set up where the store is created. Templates don't read this — they use the reactive
// `messages` store computed — so this only drives getMessage()/formatIntl() called from JS.
let currentLocale = DEFAULT_LOCALE

export function setCurrentLocale (locale) {
  if (locale && LOCALE_TABLES[locale]) {
    currentLocale = locale
  }
}

export function getCurrentLocale () {
  return currentLocale
}

function rawMessage (locale, key) {
  const table = LOCALE_TABLES[locale]
  const value = table && table[key]
  return typeof value === 'string' ? value : null
}

// Parsed format-message AST for (locale, key), cached. Always returns a valid AST: the chosen
// locale's string, else the en-US string, else the key itself (so nothing renders empty).
const astCache = {}
export function resolveAst (locale, key) {
  if (key.lastIndexOf('intl.', 0) === 0) {
    key = key.slice(5)
  }
  const cacheKey = locale + ' ' + key
  if (cacheKey in astCache) {
    return astCache[cacheKey]
  }
  let raw = rawMessage(locale, key)
  if (raw === null) {
    raw = rawMessage(DEFAULT_LOCALE, key) // fall back to English
  }
  if (raw === null) {
    raw = key // last resort: show the key, never empty
  }
  const ast = parse(raw.trim().replace(/\s+/g, ' '))
  astCache[cacheKey] = ast
  return ast
}

// If a message has no placeholders, return it as a plain string; otherwise null (needs interpret).
// An empty value (e.g. a deliberately blank key like notifyOnThisDevicePre in some locales) parses
// to an empty AST and must resolve to '' — not be dropped (which rendered "undefined") and never be
// passed to interpret() (interpret() on an empty AST throws, which crashed re-renders).
function astToPlainString (ast) {
  if (ast.length === 0) {
    return ''
  }
  if (ast.length === 1 && typeof ast[0] === 'string') {
    return ast[0]
  }
  return null
}

// Resolve a no-placeholder message to a plain string (script-side, current locale).
export function getMessage (key) {
  const ast = resolveAst(currentLocale, key)
  const plain = astToPlainString(ast)
  if (plain !== null) {
    return plain
  }
  // A placeholder message used without params — best effort so it still renders something.
  return interpret(ast, currentLocale)({})
}

// Resolve + interpret a (usually placeholder) message with values, against the current locale.
// Accepts a key string (the runtime path). For backward compatibility during the loader migration
// it also accepts an already-parsed AST (array) produced by the old build-time inlining loader.
export function formatMessage (keyOrAst, values) {
  const ast = typeof keyOrAst === 'string' ? resolveAst(currentLocale, keyOrAst) : keyOrAst
  // No-placeholder (incl. empty) messages: return as-is — interpret() on an empty AST throws.
  const plain = astToPlainString(ast)
  if (plain !== null) {
    return plain
  }
  const res = interpret(ast, currentLocale)(values)
  return (typeof res === 'string' ? res : String(res)).trim().replace(/\s+/g, ' ')
}

// Flat map of all no-placeholder messages for a locale, used by the reactive `messages` store
// computed so templates can do `$messages.someKey` and re-render on a language switch. Placeholder
// messages are omitted (templates only ever use bare/simple keys; placeholder ones go via
// formatIntl in component scripts).
const mapCache = {}
export function getMessagesMap (locale) {
  if (mapCache[locale]) {
    return mapCache[locale]
  }
  const keys = new Set([
    ...Object.keys(LOCALE_TABLES[DEFAULT_LOCALE] || {}),
    ...Object.keys(LOCALE_TABLES[locale] || {})
  ])
  const map = {}
  for (const key of keys) {
    const plain = astToPlainString(resolveAst(locale, key))
    if (plain !== null) {
      map[key] = plain
    }
  }
  mapCache[locale] = map
  return map
}
